import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import { Navigator } from "../";
import Header from "../Header/Header";
import Header2 from "../Header2/Header2";
import { SectionShellContainer } from "./SectionShell.styled";
import { useContacts } from "../../context/ContactsContext";

const Sentinel = styled.div`
  height: 1px;
`;

const Spacer = styled.div`
  height: ${(p) => p.h || 0}px;
`;

const NavigatorHeaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.theme?.spacing?.md ?? '12px'};
  padding-top: ${(p) => p.theme?.spacing?.md ?? '8px'};
  padding-inline: 25px;
  align-items: stretch;
`;

const SectionShell = ({ children, onOpenChat }) => {
    const [showCompact, setShowCompact] = useState(false);
    const [barH, setBarH] = useState(0);
    const [containerRect, setContainerRect] = useState({ width: null, left: null });
    const sentinelRef = useRef(null);
    const barRef = useRef(null);
    let debounceTimer = useRef(null);
    const { contactId } = useParams();
    const { fetchContactDetails, clearContactDetails } = useContacts();

    useEffect(() => {
        if (!sentinelRef.current) return;

        const observer = new IntersectionObserver(
            (entries) => {
                const isVisible = entries[0].isIntersecting;

                if (debounceTimer.current) clearTimeout(debounceTimer.current);
                debounceTimer.current = setTimeout(() => {
                    setShowCompact(!isVisible);
                }, 30);
            },
            {
                root: null,
                threshold: 0,
                rootMargin: "-8px 0px 0px 0px", 
            }
        );

        observer.observe(sentinelRef.current);
        return () => {
            observer.disconnect();
            if (debounceTimer.current) clearTimeout(debounceTimer.current);
        };
    }, []);

    useLayoutEffect(() => {
        const measure = () => {
            if (barRef.current) setBarH(barRef.current.offsetHeight || 0);
        };
        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, []);

    useLayoutEffect(() => {
        const scrollContainer = barRef.current?.parentElement;
        if (!scrollContainer) return;

        let mounted = true;
        let rafId = null;
        const prevRef = { width: null, left: null };

        const updateRect = () => {
            if (!mounted) return;
            const rect = scrollContainer.getBoundingClientRect();
            const width = Math.round(rect.width);
            const left = Math.round(rect.left);
            if (prevRef.width === width && prevRef.left === left) return;
            prevRef.width = width;
            prevRef.left = left;
            setContainerRect({ width, left });
        };

        const throttledUpdate = () => {
            if (rafId != null) return;
            rafId = requestAnimationFrame(() => {
                rafId = null;
                updateRect();
            });
        };

        updateRect();
        const observer = new ResizeObserver(throttledUpdate);
        observer.observe(scrollContainer);
        return () => {
            mounted = false;
            if (rafId != null) cancelAnimationFrame(rafId);
            observer.disconnect();
        };
    }, []);

    // Load contact data when contactId is present in URL
    useEffect(() => {
        if (contactId) {
            // Reset contact state before loading new contact to avoid showing previous contact data
            clearContactDetails();
            fetchContactDetails(contactId);
        }
    }, [contactId, fetchContactDetails, clearContactDetails]);

    return (
        <>
            
            <SectionShellContainer
                $show={showCompact}
                $containerWidth={containerRect.width}
                $containerLeft={containerRect.left}
                ref={barRef}
            >
                <Header2 onOpenChat={onOpenChat} />
            </SectionShellContainer>
            
            {showCompact ? <Spacer h={barH} /> : null}

            <NavigatorHeaderWrapper>
              <Navigator />
              <Sentinel ref={sentinelRef} />
              <Header onOpenChat={onOpenChat} />
            </NavigatorHeaderWrapper>

            {children}
        </>
    );
};

export default SectionShell;
